import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useToasts } from "../../LayoutComponents/ToastContext";
import i18n from "../../Services/i18n";
import AccountDataService from "../../Services/accountDataService";
import ClientService from "../../Services/clientService";
import HorseService from "../../Services/horseService";
import PrivilegeValidationService from "../../Services/privilegeValidationService";
import ClientQuickForm from "../QuickFormComponents/ClientQuickForm/ClientQuickForm";
import HorseQuickForm from "../QuickFormComponents/HorseQuickForm/HorseQuickForm";
import ListCardContainer from "../ListCard/ListCardContainer";
import { useModal } from "../../Context/ModalContext";
import styles from "./BoardServiceClient.module.css";
import UpsertServiceOccurenceModal from "../Modal/UpsertServiceOccurenceModal";
import horseIcon from "../../Assets/Icons/horse_black.png";
import clientIcon from "../../Assets/Icons/client_black.png";
import { usePrivileges } from "../../Context/PrivilegeContext";
import ServiceOccurenceService from "../../Services/serviceOccurenceService";

const BoardServiceClient = () => {
  const [boardData, setBoardData] = useState({
    clients: [],
    horses: [],
    services: [],
  });
  const [selectedClient, setSelectedClient] = useState();
  const [selectedHorse, setSelectedHorse] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [formKeys, setFormKeys] = useState({
    formClient: 0,
    formHorse: 0,
    formServiceOccurence: 0,
  });
  const [pendingSelection, setPendingSelection] = useState(null);
  const [pendingScrollRestore, setPendingScrollRestore] = useState(null);

  const clientQuickFormRef = useRef(null);
  const horseQuickFormRef = useRef(null);
  const serviceModalRef = useRef(null);
  const clientListRef = useRef(null);
  const horseListRef = useRef(null);
  const serviceListRef = useRef(null);

  const { addToast } = useToasts();
  const { openModal } = useModal();
  const { privileges } = usePrivileges();

  const fetchData = async (selectType = "", selectId = null, listKey = null) => {
    try {
      setIsLoading(true);

      const currentClientId = selectedClient?.externalId ?? null;
      const currentHorseId = selectedHorse?.externalId ?? null;

      const data = await AccountDataService.GetBoardServiceClientData();
      setBoardData(data);

      if (selectType && selectId) {
        setPendingSelection({
          type: selectType,
          id: selectId,
          listKey,
          step: "prepare",
        });
      } else {
        setPendingSelection(null);
      }

      switch (selectType) {
        case "client":
          setPendingScrollRestore({
            clientId: selectId,
            horseId: null,
            serviceId: null,
          });
          break;

        case "horse":
          setPendingScrollRestore({
            clientId: currentClientId,
            horseId: selectId,
            serviceId: null,
          });
          break;

        case "service":
          setPendingScrollRestore({
            clientId: currentClientId,
            horseId: currentHorseId,
            serviceId: selectId,
          });
          break;

        default:
          setPendingScrollRestore(null);
          break;
      }
    } catch (ex) {
      console.log(ex);
      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
      setBoardData({
        clients: [],
        horses: [],
        services: [],
      });
      setPendingSelection(null);
      setPendingScrollRestore(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshHorses();

    if (boardData.clients.length > 0) {
      refreshServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardData]);

  useLayoutEffect(() => {
    if (!pendingSelection) return;

    const { type, id, listKey, step } = pendingSelection;

    if (type === "client") {
      const newClient = boardData.clients?.find((x) => x.externalId === id);

      if (newClient) {
        setSelectedClient(newClient);
        setSelectedHorse({ externalId: -1 });
        clientListRef.current?.scrollToItem(id);
      }

      setPendingSelection(null);
      return;
    }

    if (type === "horse") {
      if (step === "prepare") {
        const parentClient = boardData.clients?.find((client) =>
          client.horses?.some((horse) => horse.externalId === id),
        );

        if (!parentClient) {
          setPendingSelection(null);
          return;
        }

        setSelectedClient(parentClient);

        setPendingSelection({
          type: "horse",
          id,
          listKey,
          step: "apply",
        });

        return;
      }

      if (step === "apply") {
        const currentClient = boardData.clients?.find(
          (x) => x.externalId === selectedClient?.externalId,
        );

        const newHorse = currentClient?.horses?.find((x) => x.externalId === id);

        if (newHorse) {
          setSelectedHorse(newHorse);
          horseListRef.current?.scrollToItem(id);
        }

        setPendingSelection(null);
        return;
      }
    }

    if (type === "service") {
      serviceListRef.current?.scrollToItem(id);
      setPendingSelection(null);
    }
  }, [boardData, pendingSelection, selectedClient]);

  useLayoutEffect(() => {
    if (!pendingScrollRestore) return;

    const { clientId, horseId, serviceId } = pendingScrollRestore;

    if (clientId) {
      clientListRef.current?.scrollToItem(clientId);
    }

    if (horseId && horseId !== -1) {
      horseListRef.current?.scrollToItem(horseId);
    }

    if (serviceId) {
      serviceListRef.current?.scrollToItem(serviceId);
    }

    setPendingScrollRestore(null);
  }, [boardData, selectedClient, selectedHorse, pendingScrollRestore]);

  useLayoutEffect(() => {
    if (!pendingScrollRestore) return;

    const { clientId, horseId, serviceId } = pendingScrollRestore;

    if (clientId) {
      clientListRef.current?.scrollToItem(clientId);
    }

    if (horseId && horseId !== -1) {
      horseListRef.current?.scrollToItem(horseId);
    }

    if (serviceId) {
      serviceListRef.current?.scrollToItem(serviceId);
    }

    setPendingScrollRestore(null);
  }, [boardData, selectedClient, selectedHorse, pendingScrollRestore]);

  const refreshHorses = () => {
    if (!selectedClient) return;

    const refreshedClient = boardData.clients.find(
      (x) => x.externalId === selectedClient.externalId,
    );

    setSelectedClient(refreshedClient);
  };

  const refreshServices = () => {
    if (!selectedClient) return;

    const refreshedClient = boardData.clients.find(
      (x) => x.externalId === selectedClient.externalId,
    );

    if (!refreshedClient) {
      setSelectedClient(undefined);
      setSelectedHorse(undefined);
      return;
    }

    if (!selectedHorse || selectedHorse.externalId === -1) {
      setSelectedClient(refreshedClient);
    } else {
      const refreshedHorse = refreshedClient.horses?.find(
        (x) => x.externalId === selectedHorse.externalId,
      );

      setSelectedClient(refreshedClient);
      setSelectedHorse(refreshedHorse);
    }
  };

  const handleCloseForm = (formName) => {
    setFormKeys((prevKeys) => ({
      ...prevKeys,
      [formName]: prevKeys[formName] + 1,
    }));
  };

  const handleClientSelect = (client) => {
    if (selectedClient !== client) {
      setSelectedClient(client);
      setSelectedHorse({ externalId: -1 });
    }
  };

  const handleAddClientClick = async () => {
    if (!PrivilegeValidationService.CanAddClient(await privileges(), boardData.clients.length)) {
      addToast(
        i18n.t("Privilege.ValidationError.MaxClients"),
        "PrivilegeValidationFailed",
        "ERROR",
        true,
      );
      return;
    }

    openModal({
      title: i18n.t("BoardServiceClient.ClientQuickCreate"),
      form: "formClient",
      wideMode: true,
      hideSaveButton: true,
      hideSaveAndCloseButton: false,
      content: <ClientQuickForm key={formKeys.formClient} ref={clientQuickFormRef} />,
      canClose: async () => {
        const isDirty = clientQuickFormRef.current?.isDirty?.();
        if (!isDirty) return true;

        return window.confirm(i18n.t("Modal.UnsavedChangesConfirm"));
      },
      onSave: async () => {
        const isValid = clientQuickFormRef.current.validate();
        if (!isValid) return false;

        const resultId = await ClientService.QuickCreate(clientQuickFormRef.current.getFormData());
        if (resultId) {
          await fetchData("client", resultId, "client");
          return true;
        } else {
          return false;
        }
      },
      onClose: () => {
        handleCloseForm("formClient");
      },
    });
  };

  const handleAddHorseClick = async () => {
    if (!selectedClient) return;

    if (!PrivilegeValidationService.CanAddHorse(await privileges(), selectedClient.horses.length)) {
      addToast(
        i18n.t("Privilege.ValidationError.MaxServicesByClient"),
        "PrivilegeValidationFailed",
        "ERROR",
        true,
      );
      return;
    }

    openModal({
      title: i18n.t("BoardServiceClient.HorseQuickCreate"),
      form: "formHorse",
      wideMode: true,
      hideSaveButton: true,
      hideSaveAndCloseButton: false,
      canClose: async () => {
        const isDirty = horseQuickFormRef.current?.isDirty?.();
        if (!isDirty) return true;

        return window.confirm(i18n.t("Modal.UnsavedChangesConfirm"));
      },
      content: (
        <HorseQuickForm key={formKeys.formHorse} ref={horseQuickFormRef} client={selectedClient} />
      ),
      onSave: async () => {
        const isValid = horseQuickFormRef.current.validate();
        if (!isValid) return false;

        const resultId = await HorseService.QuickCreate(horseQuickFormRef.current.getFormData());

        if (resultId) {
          await fetchData("horse", resultId, "horse");
          return true;
        } else {
          return false;
        }
      },
      onClose: () => {
        handleCloseForm("formHorse");
      },
    });
  };

  const handleAddServiceClick = async () => {
    const services = boardData.services.filter((x) => {
      if (selectedHorse?.externalId === -1) return x.isForClients;
      if (selectedHorse) return x.isForHorses;
      return false;
    });

    if (services.length === 0) {
      addToast(
        i18n.t("BoardServiceClient.ServiceOccurenceNoService"),
        "SERVICEOCCURENCENOSERVICE",
        "WARNING",
        true,
      );
      return;
    }

    let modalIcon;
    let modalTitle;
    if (selectedHorse?.externalId === -1) {
      modalIcon = clientIcon;
      modalTitle = i18n.t("BoardServiceClient.ServiceOccurenceCreateClient");
      if (
        !PrivilegeValidationService.ClientCanAddService(
          await privileges(),
          selectedClient.services.length,
        )
      ) {
        addToast(
          i18n.t("Privilege.ValidationError.MaxServicesByClient"),
          "PrivilegeValidationFailed",
          "ERROR",
          true,
        );
        return;
      }
    } else {
      modalIcon = horseIcon;
      modalTitle = i18n.t("BoardServiceClient.ServiceOccurenceCreateHorse");
      if (
        !PrivilegeValidationService.HorseCanAddService(
          await privileges(),
          selectedHorse.services.length,
        )
      ) {
        addToast(
          i18n.t("Privilege.ValidationError.MaxServicesByHorse"),
          "PrivilegeValidationFailed",
          "ERROR",
          true,
        );
        return;
      }
    }

    const hideGenerateReportBtn = await serviceModalRef.current?.serviceOccurenceExternalId();

    setTimeout(async () => {
      openModal({
        title: (
          <>
            <img src={modalIcon} alt="client icon" className={styles.headericon} />
            {modalTitle}
          </>
        ),
        form: "formServiceOccurence",
        wideMode: true,
        hideSaveButton: false,
        hideSaveAndCloseButton: false,
        generateReportDisabled: hideGenerateReportBtn,
        generateReportLabel: i18n.t("Modal.GenerateReport.ServiceReport"),
        canClose: async () => {
          const isDirty = serviceModalRef.current?.isDirty?.();
          if (!isDirty) return true;

          return window.confirm(i18n.t("Modal.UnsavedChangesConfirm"));
        },
        handleGenerateReport: async () => {
          await ServiceOccurenceService.GenerateServiceReport();
        },
        onSave: async () => {
          const isValid = await serviceModalRef.current.validate();
          if (!isValid) return false;

          return await serviceModalRef.current.save();
        },
        onClose: () => {},
        content: (
          <UpsertServiceOccurenceModal
            key={formKeys.formServiceOccurence}
            ref={serviceModalRef}
            client={selectedClient}
            horse={selectedHorse}
            onSave={async (newServiceId) => {
              await fetchData("service", newServiceId, "service");
            }}
            onClose={() => handleCloseForm("formServiceOccurence")}
          />
        ),
      });
    });
  };

  const renderHorsesAndServices = () => {
    const getServicesheaderName = () => {
      if (selectedHorse && selectedHorse.externalId !== -1) {
        return selectedHorse.cardName;
      }

      if (selectedClient) {
        return selectedClient.cardName;
      }

      return undefined;
    };

    return (
      <div className={styles.horseAndService}>
        <div className={styles.horse}>
          <ListCardContainer
            headerLabel={i18n.t("BoardServiceClient.HorseListHeader")}
            addButtonAction={handleAddHorseClick}
            addButtonDisabled={!selectedClient}
            isLoading={isLoading}
            dataItems={selectedClient?.horses ?? []}
            onSelect={setSelectedHorse}
            refresh={fetchData}
            selectedItemExternalId={selectedHorse?.externalId}
            type="horse"
            ref={horseListRef}
            hideEmptyHorseCard={!selectedClient}
            storageId={"BoardServiceClient.Horse"}
          />
        </div>

        <div className={styles.service}>
          <ListCardContainer
            isLoading={isLoading}
            headerLabel={i18n.t("BoardServiceClient.ServiceListHeader")}
            headerSubLabel={getServicesheaderName()}
            addButtonAction={handleAddServiceClick}
            addButtonDisabled={!selectedClient}
            dataItems={
              (selectedHorse?.externalId === -1
                ? selectedClient?.services
                : selectedHorse?.services) ?? []
            }
            onSelect={() => {}}
            refresh={fetchData}
            selectedItemExternalId={selectedHorse?.externalId}
            type="serviceoccurence"
            ref={serviceListRef}
            storageId={"BoardServiceClient.Service"}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.client}>
        <ListCardContainer
          isLoading={isLoading}
          headerLabel={i18n.t("BoardServiceClient.ClientListHeader")}
          addButtonAction={handleAddClientClick}
          dataItems={boardData?.clients ?? []}
          onSelect={handleClientSelect}
          selectedItemExternalId={selectedClient?.externalId}
          refresh={fetchData}
          type="client"
          ref={clientListRef}
          storageId={"BoardServiceClient.Client"}
        />
      </div>

      {renderHorsesAndServices()}
    </div>
  );
};

export default BoardServiceClient;
